import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { Payment as PaymentIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { FormattedMessage, useIntl } from "react-intl";
import {
  fetchPricesApi,
  updateManualPaymentApi,
} from "../../../utils/api/manualPayment";
import { PaymentHistoryItem } from "../../../redux/types/types";
import Notiflix from "notiflix";

interface EditManualPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  payment: PaymentHistoryItem | null;
  onSuccess?: () => void;
}

interface Price {
  id: number;
  numberOfClasses: number;
  specialType: string | null;
  amount: number;
  currency: string;
  isAdminOnly: boolean;
}

export default function EditManualPaymentDialog({
  open,
  onClose,
  payment,
  onSuccess,
}: EditManualPaymentDialogProps) {
  const theme = useTheme();
  const intl = useIntl();
  const [prices, setPrices] = useState<Price[]>([]);
  const [selectedPriceId, setSelectedPriceId] = useState<number | "">("");
  const [selectedPrice, setSelectedPrice] = useState<Price | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchingPrices, setFetchingPrices] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatPrice = (amount: number) => {
    return (amount / 100).toFixed(2);
  };

  const handleClose = () => {
    setSelectedPriceId("");
    setSelectedPrice(null);
    setError(null);
    onClose();
  };

  useEffect(() => {
    if (open && payment) {
      setFetchingPrices(true);
      setError(null);

      fetchPricesApi()
        .then((response) => {
          setPrices(response.data);
          // Set current price as selected
          if (payment.priceId) {
            setSelectedPriceId(payment.priceId);
            const currentPrice = response.data.find(
              (p: Price) => p.id === payment.priceId
            );
            if (currentPrice) {
              setSelectedPrice(currentPrice);
            }
          }
        })
        .catch((error) => {
          console.error("Failed to fetch prices:", error);
          setError(
            intl.formatMessage({ id: "manualPayment.fetchPricesError" })
          );
        })
        .finally(() => {
          setFetchingPrices(false);
        });
    } else {
      setSelectedPriceId("");
      setSelectedPrice(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payment]);

  useEffect(() => {
    if (selectedPriceId && prices.length > 0) {
      const price = prices.find((p) => p.id === selectedPriceId);
      setSelectedPrice(price || null);
    } else {
      setSelectedPrice(null);
    }
  }, [selectedPriceId, prices]);

  const handleUpdatePayment = async () => {
    if (!selectedPriceId || !payment) {
      Notiflix.Notify.warning(
        intl.formatMessage({ id: "manualPayment.noPrice" })
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await updateManualPaymentApi(payment.id, {
        priceId: selectedPriceId as number,
      });

      Notiflix.Notify.success(
        intl.formatMessage({ id: "admin.paymentHistory.updateSuccess" })
      );

      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error("Failed to update manual payment:", error);
      setError(intl.formatMessage({ id: "admin.paymentHistory.updateError" }));
      Notiflix.Notify.failure(
        intl.formatMessage({ id: "admin.paymentHistory.updateError" })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PaymentIcon color="primary" />
          <FormattedMessage
            id="admin.paymentHistory.editPayment"
            defaultMessage="Edit Manual Payment"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {payment && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              <FormattedMessage
                id="admin.paymentHistory.attendeeInfo"
                defaultMessage="Attendee Information"
              />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`${payment.attendee.firstName} ${payment.attendee.lastName} (${payment.attendee.email})`}
            </Typography>
          </Box>
        )}

        {fetchingPrices ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <FormControl fullWidth>
            <InputLabel id="price-select-label">
              <FormattedMessage
                id="manualPayment.selectPrice"
                defaultMessage="Select Price"
              />
            </InputLabel>
            <Select
              labelId="price-select-label"
              value={selectedPriceId}
              label={intl.formatMessage({ id: "manualPayment.selectPrice" })}
              onChange={(e) => setSelectedPriceId(e.target.value as number)}
            >
              {prices.map((price) => (
                <MenuItem key={price.id} value={price.id}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>
                        {price.specialType ? (
                          <>
                            {price.specialType} ({price.numberOfClasses}{" "}
                            <FormattedMessage id="manualPayment.classes" />)
                          </>
                        ) : (
                          <>
                            {price.numberOfClasses}{" "}
                            <FormattedMessage id="manualPayment.classes" />
                          </>
                        )}
                      </span>
                      {price.isAdminOnly && (
                        <Chip label={<FormattedMessage id="payment.admin" />} color="warning" size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                      )}
                    </Box>
                    <span>
                      {formatPrice(price.amount)}{" "}
                      <FormattedMessage id="manualPayment.currency" />
                      {price.isAdminOnly && (
                        <Typography component="span" variant="caption" sx={{ ml: 0.5, color: "warning.main" }}>
                          (<FormattedMessage id="payment.free" />)
                        </Typography>
                      )}
                    </span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {selectedPrice && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: theme.palette.mode === "dark" ? "grey.800" : "grey.50",
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography variant="h6">
                <FormattedMessage
                  id="admin.dashboard.selectedPrice"
                  defaultMessage="Selected Price:"
                />
              </Typography>
              {selectedPrice.isAdminOnly && (
                <Chip label={<FormattedMessage id="payment.adminOnly" />} color="warning" size="small" />
              )}
            </Box>
            <Typography variant="body2">
              <FormattedMessage id="manualPayment.amount" />:{" "}
              {formatPrice(selectedPrice.amount)}{" "}
              <FormattedMessage id="manualPayment.currency" />
              {selectedPrice.isAdminOnly && (
                <Typography component="span" variant="caption" sx={{ ml: 0.5, color: "warning.main" }}>
                  (<FormattedMessage id="payment.free" />)
                </Typography>
              )}
            </Typography>
            <Typography variant="body2">
              <FormattedMessage id="manualPayment.classes" />:{" "}
              {selectedPrice.numberOfClasses}
            </Typography>
            {selectedPrice.specialType && (
              <Typography variant="body2">
                <FormattedMessage id="manualPayment.specialType" />:{" "}
                {selectedPrice.specialType}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="secondary">
          <FormattedMessage id="manualPayment.cancel" />
        </Button>
        <Button
          onClick={handleUpdatePayment}
          variant="contained"
          color="primary"
          disabled={!selectedPriceId || loading || fetchingPrices}
          startIcon={loading ? <CircularProgress size={16} /> : <PaymentIcon />}
        >
          <FormattedMessage
            id="admin.paymentHistory.updatePayment"
            defaultMessage="Update Payment"
          />
        </Button>
      </DialogActions>
    </Dialog>
  );
}
